import * as PIXI from 'pixi.js';
import type { HealthBar } from './health-bar-renderer.js';

/**
 * Active flash effect data structure
 * Tracks filter instance and timing information for ongoing flash animation
 */
interface ActiveFlashEffect {
  filter: PIXI.ColorMatrixFilter;
  startTime: number;
  duration: number;
  unitId: string;
  lastFlashTime: number; // For min interval check (accessibility)
  healthBar: HealthBar; // CRITICAL: Store healthBar reference to clear filters on cancel
}

/**
 * L4-BATCH1-007: Active execute range effect data structure
 * Tracks pulsing glow effect for units in execute range (below 15% HP)
 */
interface ActiveExecuteRangeEffect {
  filter: PIXI.ColorMatrixFilter;
  unitId: string;
  healthBar: HealthBar;
  startTime: number;
}

/**
 * HealthBarEffectsManager
 *
 * Manages visual effects for health bars, specifically flash-on-damage effects.
 * Separated from HealthBarRenderer to maintain single responsibility principle.
 *
 * Key Features:
 * - GPU-accelerated flash using ColorMatrixFilter
 * - Accessibility-compliant minimum flash interval (300ms)
 * - Subtle brightness increase (1.3x) to prevent overwhelming visuals
 * - Cancel-on-new strategy to prevent overlapping flashes
 * - Proper cleanup to prevent memory leaks
 *
 * Performance:
 * - Uses Map for O(1) lookup of active effects
 * - ColorMatrixFilter is GPU-accelerated
 * - Typically handles < 10 concurrent flashes
 *
 * @example
 * const effectsManager = new HealthBarEffectsManager();
 *
 * // Trigger flash when unit takes damage
 * effectsManager.triggerFlash(unitId, healthBar);
 *
 * // Update in render loop
 * effectsManager.update(Date.now());
 *
 * // Cleanup when health bar removed
 * effectsManager.cancelFlash(unitId);
 */
export class HealthBarEffectsManager {
  /** Active flash effects indexed by unit ID */
  private activeFlashes: Map<string, ActiveFlashEffect> = new Map();

  /** L4-BATCH1-007: Active execute range effects indexed by unit ID */
  private activeExecuteRangeEffects: Map<string, ActiveExecuteRangeEffect> = new Map();

  // Configuration Constants

  /** Duration of flash effect in milliseconds */
  private readonly FLASH_DURATION = 150;

  /** Minimum time between flashes (accessibility: prevents rapid flashing) */
  private readonly FLASH_MIN_INTERVAL = 300;

  /** Brightness multiplier (1.3 = 30% brighter, subtle to avoid overwhelming) */
  private readonly FLASH_BRIGHTNESS = 1.3;

  /** L4-BATCH1-007: Execute range threshold (15% HP) */
  private readonly EXECUTE_THRESHOLD = 0.15;

  /** L4-BATCH1-007: Execute range pulse duration in milliseconds (full cycle) */
  private readonly EXECUTE_PULSE_DURATION = 1000;

  /**
   * Trigger a white flash effect on a health bar
   *
   * Implements cancel-on-new strategy: if a flash is already active and the
   * minimum interval has passed, the old flash is cancelled and a new one starts.
   * If the minimum interval has not passed, the flash is skipped (accessibility).
   *
   * Uses ColorMatrixFilter for GPU-accelerated brightness adjustment.
   *
   * @param unitId - Unique identifier for the unit whose health bar should flash
   * @param healthBar - HealthBar instance to apply the flash effect to
   *
   * @example
   * // Trigger flash when unit takes damage
   * effectsManager.triggerFlash('unit-123', healthBar);
   */
  public triggerFlash(unitId: string, healthBar: HealthBar): void {
    const currentTime = Date.now();
    const existingFlash = this.activeFlashes.get(unitId);

    // Accessibility: Check minimum interval to prevent rapid flashing
    if (existingFlash) {
      const timeSinceLastFlash = currentTime - existingFlash.lastFlashTime;
      if (timeSinceLastFlash < this.FLASH_MIN_INTERVAL) {
        // Too soon since last flash, skip this one
        return;
      }
      // Minimum interval passed, cancel old flash and start new
      this.cancelFlash(unitId);
    }

    // Create GPU-accelerated ColorMatrixFilter for brightness effect
    const flashFilter = new PIXI.ColorMatrixFilter();
    flashFilter.brightness(this.FLASH_BRIGHTNESS, true); // true = multiply mode

    // Apply filter to health bar container
    // Note: This will override any existing filters on the container
    healthBar.container.filters = [flashFilter];

    // Track the active flash effect
    const flashEffect: ActiveFlashEffect = {
      filter: flashFilter,
      startTime: currentTime,
      duration: this.FLASH_DURATION,
      unitId,
      lastFlashTime: currentTime,
      healthBar // CRITICAL: Store healthBar reference for cleanup
    };

    this.activeFlashes.set(unitId, flashEffect);
  }

  /**
   * Update all active flash effects
   *
   * Should be called every frame from the render loop. Updates the brightness
   * of all active flashes based on elapsed time, using easeOutCubic for smooth
   * animation. Automatically cleans up completed flashes.
   *
   * @param currentTime - Current timestamp (from Date.now() or performance.now())
   *
   * @example
   * // Call in render loop
   * function renderFrame() {
   *   effectsManager.update(Date.now());
   * }
   */
  public update(currentTime: number): void {
    // Iterate over all active flash effects
    // Using forEach to avoid downlevelIteration requirement
    this.activeFlashes.forEach((flash, unitId) => {
      // Calculate progress (0.0 to 1.0)
      const elapsed = currentTime - flash.startTime;
      const progress = Math.min(elapsed / flash.duration, 1.0);

      // Apply easing for smooth fade-out
      const easedProgress = this.easeOutCubic(progress);

      // Interpolate brightness from FLASH_BRIGHTNESS back to 1.0 (normal)
      // Formula: current = 1.0 + ((max - 1.0) * (1.0 - progress))
      const brightness = 1.0 + ((this.FLASH_BRIGHTNESS - 1.0) * (1.0 - easedProgress));

      // Update filter brightness
      flash.filter.brightness(brightness, true);

      // Check if flash is complete
      if (progress >= 1.0) {
        // Flash complete, cleanup
        this.cancelFlash(unitId);
      }
    });
  }

  /**
   * Cancel an active flash effect and perform cleanup
   *
   * Removes the flash effect from the active map and destroys the filter
   * to prevent memory leaks. Safe to call even if no flash is active.
   *
   * CRITICAL FIX: This method MUST clear the container's filters BEFORE
   * destroying the filter. Otherwise, the container will still reference
   * the destroyed filter and cause "Cannot set properties of null" errors
   * during rendering.
   *
   * @param unitId - Unique identifier for the unit whose flash should be cancelled
   *
   * @example
   * // Cancel flash when health bar is removed
   * effectsManager.cancelFlash('unit-123');
   */
  public cancelFlash(unitId: string): void {
    const flash = this.activeFlashes.get(unitId);

    if (flash) {
      // CRITICAL FIX: Clear the container's filters BEFORE destroying the filter
      // This prevents the "Cannot set properties of null" error that occurs when
      // PixiJS tries to render a container with a destroyed filter
      flash.healthBar.container.filters = [];

      // Destroy the filter to prevent memory leak
      flash.filter.destroy();

      // Remove from active flashes map
      this.activeFlashes.delete(unitId);
    }
  }

  /**
   * Ease-out cubic easing function
   *
   * Provides smooth deceleration for the flash fade-out animation.
   * Starts fast and slows down toward the end, creating a natural-feeling effect.
   *
   * Mathematical formula: 1 - (1 - t)³
   *
   * @param t - Progress value from 0.0 to 1.0
   * @returns Eased value from 0.0 to 1.0
   *
   * @private
   */
  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  /**
   * L4-BATCH1-007: Update execute range indicator based on unit's health
   *
   * When a unit falls below 15% HP, adds a pulsing red glow effect to their
   * health bar. When health rises above 15%, removes the effect.
   *
   * @param unitId - Unique identifier for the unit
   * @param healthBar - HealthBar instance to apply the effect to
   * @param currentHealth - Current health of the unit
   * @param maxHealth - Maximum health of the unit
   */
  public updateExecuteRangeIndicator(
    unitId: string,
    healthBar: HealthBar,
    currentHealth: number,
    maxHealth: number
  ): void {
    const healthPercent = maxHealth > 0 ? currentHealth / maxHealth : 0;
    const isInExecuteRange = healthPercent > 0 && healthPercent <= this.EXECUTE_THRESHOLD;
    const hasEffect = this.activeExecuteRangeEffects.has(unitId);

    if (isInExecuteRange && !hasEffect) {
      // Unit just entered execute range - add effect
      this.startExecuteRangeEffect(unitId, healthBar);
    } else if (!isInExecuteRange && hasEffect) {
      // Unit left execute range - remove effect
      this.cancelExecuteRangeEffect(unitId);
    }
  }

  /**
   * L4-BATCH1-007: Start the execute range pulsing effect
   */
  private startExecuteRangeEffect(unitId: string, healthBar: HealthBar): void {
    // Don't start if a flash is currently active (flash takes priority)
    if (this.activeFlashes.has(unitId)) {
      return;
    }

    const filter = new PIXI.ColorMatrixFilter();
    // Initial red tint
    filter.saturate(0.5, true);

    // Only apply if no other filters are active
    if (!healthBar.container.filters || healthBar.container.filters.length === 0) {
      healthBar.container.filters = [filter];
    }

    const effect: ActiveExecuteRangeEffect = {
      filter,
      unitId,
      healthBar,
      startTime: Date.now()
    };

    this.activeExecuteRangeEffects.set(unitId, effect);
  }

  /**
   * L4-BATCH1-007: Update all execute range effects (pulsing animation)
   *
   * Should be called in the same update loop as update().
   * Creates a subtle pulsing red glow effect.
   *
   * @param currentTime - Current timestamp
   */
  public updateExecuteRangeEffects(currentTime: number): void {
    this.activeExecuteRangeEffects.forEach((effect) => {
      // Don't update if flash is active (flash takes visual priority)
      if (this.activeFlashes.has(effect.unitId)) {
        return;
      }

      // Calculate pulse progress (0 to 1 to 0 in a sine wave pattern)
      const elapsed = currentTime - effect.startTime;
      const cycleProgress = (elapsed % this.EXECUTE_PULSE_DURATION) / this.EXECUTE_PULSE_DURATION;

      // Use sine wave for smooth pulsing (0 -> 1 -> 0)
      const pulseIntensity = Math.sin(cycleProgress * Math.PI * 2) * 0.5 + 0.5;

      // Apply red tint with varying intensity (subtle effect)
      // Brightness ranges from 1.0 to 1.2 based on pulse
      const brightness = 1.0 + (pulseIntensity * 0.2);
      effect.filter.brightness(brightness, true);

      // Also add a subtle red shift using contrast
      // This creates the "execute range" visual warning
      effect.filter.saturate(1.5 - (pulseIntensity * 0.5), true);

      // Ensure filter is still applied (in case flash was cancelled)
      if (!effect.healthBar.container.filters || effect.healthBar.container.filters.length === 0) {
        effect.healthBar.container.filters = [effect.filter];
      }
    });
  }

  /**
   * L4-BATCH1-007: Cancel execute range effect for a unit
   */
  public cancelExecuteRangeEffect(unitId: string): void {
    const effect = this.activeExecuteRangeEffects.get(unitId);

    if (effect) {
      // Clear filters only if this effect's filter is still attached
      if (effect.healthBar.container.filters?.includes(effect.filter)) {
        effect.healthBar.container.filters = [];
      }

      effect.filter.destroy();
      this.activeExecuteRangeEffects.delete(unitId);
    }
  }

  /**
   * L4-BATCH1-007: Check if a unit is in execute range
   */
  public isInExecuteRange(unitId: string): boolean {
    return this.activeExecuteRangeEffects.has(unitId);
  }

  /**
   * L4-BATCH1-007: Get the execute threshold value
   */
  public getExecuteThreshold(): number {
    return this.EXECUTE_THRESHOLD;
  }
}
